import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getSupabase } from '@/integrations/supabase/lazy';
import { trackEvent } from '@/lib/analytics';

interface WishlistStore {
  items: string[]; // product handles
  _userId: string | null;
  toggle: (handle: string) => void;
  has: (handle: string) => boolean;
  count: () => number;
  syncWithCloud: () => Promise<void>;
  setUserId: (userId: string | null) => void;
}

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],
      _userId: null,

      setUserId: (userId) => {
        set({ _userId: userId });
        if (userId) get().syncWithCloud();
      },

      toggle: (handle) => {
        const { items, _userId } = get();
        const exists = items.includes(handle);
        const newItems = exists
          ? items.filter((h) => h !== handle)
          : [...items, handle];
        set({ items: newItems });

        // Track wishlist analytics
        if (!exists) {
          trackEvent("add_to_wishlist", { product_handle: handle });
        }

        // Sync to cloud in background
        if (_userId) {
          getSupabase().then((supabase) => {
            if (exists) {
              supabase
                .from('wishlist_items')
                .delete()
                .eq('user_id', _userId)
                .eq('product_handle', handle)
                .then();
            } else {
              supabase
                .from('wishlist_items')
                .insert({ user_id: _userId, product_handle: handle })
                .then();
            }
          });
        }
      },

      has: (handle) => get().items.includes(handle),
      count: () => get().items.length,

      syncWithCloud: async () => {
        const { _userId, items: localItems } = get();
        if (!_userId) return;

        try {
          const supabase = await getSupabase();
          const { data: cloudItems } = await supabase
            .from('wishlist_items')
            .select('product_handle')
            .eq('user_id', _userId);

          const cloudHandles = (cloudItems || []).map((i: any) => i.product_handle);
          // Merge: union of local + cloud
          const merged = [...new Set([...localItems, ...cloudHandles])];
          set({ items: merged });

          // Push any local-only items to cloud
          const localOnly = localItems.filter((h) => !cloudHandles.includes(h));
          if (localOnly.length > 0) {
            await supabase
              .from('wishlist_items')
              .upsert(
                localOnly.map((h) => ({ user_id: _userId, product_handle: h })),
                { onConflict: 'user_id,product_handle' }
              );
          }
        } catch (err) {
          console.error('Wishlist sync error:', err);
        }
      },
    }),
    {
      name: 'loftloom-wishlist',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);
